/**
 * Auth bridge to the Chrome extension.
 *
 * The extension's popup opens this app at `/sign-in?extension=true&extid=<id>`.
 * Once Supabase hands us a session we post the access token straight to that
 * extension id via `chrome.runtime.sendMessage`, which is the only channel a
 * web page has to an extension it does not share an origin with.
 */

interface ChromeRuntimeLike {
  runtime?: {
    sendMessage?: (extensionId: string, message: unknown, callback?: (response?: unknown) => void) => void;
    lastError?: { message?: string };
  };
}

declare const chrome: ChromeRuntimeLike | undefined;

const EXT_ID_KEY = 'lienguard.extensionId';

/**
 * The extension id, remembered from the sign-in hand-off so later pages can
 * still talk back to it. Falls back to the build-time id when one is set.
 */
export function knownExtensionId(): string | null {
  try {
    return window.localStorage.getItem(EXT_ID_KEY) || (import.meta.env.VITE_EXTENSION_ID ?? null);
  } catch {
    return import.meta.env.VITE_EXTENSION_ID ?? null;
  }
}

function rememberExtensionId(id: string): void {
  try {
    window.localStorage.setItem(EXT_ID_KEY, id);
  } catch {
    // Private-mode storage refusal is not worth failing a sign-in over.
  }
}

/** Reads `?extension=true&extid=<id>` off the current URL. */
export function extensionRequest(search: string): { wanted: boolean; extensionId: string | null } {
  const params = new URLSearchParams(search);
  const wanted = params.get('extension') === 'true';
  const fromUrl = params.get('extid');
  if (fromUrl) rememberExtensionId(fromUrl);
  return { wanted, extensionId: fromUrl || knownExtensionId() };
}

export type BridgeResult = 'sent' | 'no-extension' | 'no-id' | 'failed';

/** Where a contractor installs it: the Chrome Web Store page, once published. */
export function extensionStoreUrl(): string | null {
  const explicit = import.meta.env.VITE_EXTENSION_STORE_URL;
  if (explicit) return explicit;
  const id = import.meta.env.VITE_EXTENSION_ID;
  return id ? `https://chromewebstore.google.com/detail/${id}` : null;
}

export type ExtensionPresence =
  | { state: 'installed'; version: string | null }
  | { state: 'missing' }
  | { state: 'unsupported' };

/**
 * Is the extension installed in this browser? Only an installed extension that
 * lists this site can answer, so silence means "not installed". Not Chrome (or
 * Edge, Brave — anything Chromium) on a computer means it cannot be.
 */
export async function extensionPresence(): Promise<ExtensionPresence> {
  const chromium = /Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent) && !/Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
  if (!chromium) return { state: 'unsupported' };
  const id = knownExtensionId();
  const runtime = typeof chrome !== 'undefined' ? chrome?.runtime : undefined;
  if (!id || !runtime?.sendMessage) return { state: 'missing' };
  return new Promise<ExtensionPresence>((resolve) => {
    let settled = false;
    const done = (p: ExtensionPresence) => {
      if (!settled) {
        settled = true;
        resolve(p);
      }
    };
    try {
      runtime.sendMessage!(id, { type: 'PING' }, (response?: unknown) => {
        const r = response as { ok?: boolean; version?: string } | undefined;
        done(!runtime.lastError && r?.ok ? { state: 'installed', version: r.version ?? null } : { state: 'missing' });
      });
    } catch {
      done({ state: 'missing' });
    }
    setTimeout(() => done({ state: 'missing' }), 1500);
  });
}

export async function sendTokenToExtension(search: string, accessToken: string): Promise<BridgeResult> {
  const { wanted, extensionId } = extensionRequest(search);
  if (!wanted) return 'no-extension';
  return postToExtension(extensionId, { type: 'AUTH_TOKEN', token: accessToken });
}

/**
 * Tell the extension the job it sent the user here to create now exists, so
 * its popup can offer a link straight to it. Best-effort: the dashboard works
 * perfectly well without an extension installed.
 */
export async function notifyJobCreated(jobId: string, address: string): Promise<BridgeResult> {
  return postToExtension(knownExtensionId(), { type: 'JOB_CREATED', jobId, address });
}

async function postToExtension(extensionId: string | null, message: unknown): Promise<BridgeResult> {
  if (!extensionId) return 'no-id';
  const runtime = typeof chrome !== 'undefined' ? chrome?.runtime : undefined;
  if (!runtime?.sendMessage) return 'no-extension';

  return new Promise<BridgeResult>((resolve) => {
    let settled = false;
    // Chrome reports a bad extension id through lastError, never a rejection.
    const done = (result: BridgeResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    try {
      runtime.sendMessage!(extensionId, message, () => {
        done(runtime.lastError ? 'failed' : 'sent');
      });
    } catch {
      done('failed');
    }
    setTimeout(() => done('failed'), 2500);
  });
}
